// "Panel de cierres" — ported from mantis-business-front-brinks's src/routes/index.tsx, the
// landing page of the redesign (what the user calls "el panel de control"). One row per REAL
// closing (not per office, unlike `CierresOficinas.jsx`), with Excel-style per-column filters.
//
// B-62 — the figure columns are no longer Davivienda's by hand. They are the `resumen` group of
// the selected client's panel config (`getExtractionFields`), and "Bloqueado" is the closing's
// open blocking alerts (`getCierreAlertsBatch`, one request), exactly as the detail decides it.
//
// A client whose panel config says `list: all` (Bancamía) gets its whole CUADRE instead: every
// group, A to AK, in a wide table that scrolls sideways with Fecha and Oficina pinned to the left.
// C REGIONAL is the row's own `region`, and AK OBSERVACIONES the notes on the closing's alerts.
// Without `list: all` the table is exactly the fixed-width one below.
// What follows is the original note on brinks' seven columns, kept for the history of each one.
//
// All seven of brinks' columns are here, each backed by something real:
//   - Cuadre Total    -> `diferencia_cuadre`, a real calculation in every Davivienda profile
//                        override this session built (`profiles/clients/davivienda/**`).
//   - Cuadre DOE      -> `diferencia_doe`, declared in `panel/clients/davivienda.yaml`'s own
//                        `resumen` group — gathered, just not shown before this pass.
//   - Pendientes      -> not a flat panel field (BR-23's projection has none): read from the
//     closings' alerts the list already fetches in ONE `getCierreAlertsBatch` — a blocked alert
//     says what has not arrived ("Falta: Saldo inicial de bóveda"), an open alert a descuadre.
//     It used to fan out one `getCierreClosingDetail` per visible row, which took seconds to
//     fill the column, and counted both kinds as "faltantes".
//   - Conciliación    -> genuinely absent. No Davivienda profile computes it (that key belongs
//     to Brinks' old `jn`/`ja` process, per `mock-data.ts`) — shown as "—" on every row rather
//     than invented, the same "absent is never zero" rule as everywhere else in this pipeline.
import { useEffect, useMemo, useState } from "react";
import { connect } from "react-redux";
import {
  AlertTriangle,
  Building2,
  Check,
  Copy,
  Download,
  Filter,
  Mail,
  MoreHorizontal,
  ChevronDown,
  ArrowLeft,
  RotateCcw,
  Search,
  Shield,
  Coins,
  X,
} from "lucide-react";
import {
  Button,
  Checkbox,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Spinner,
  cn,
} from "@mantis-sas/ui";


import { selectAuthState } from "../../../store/selectors/authSelectors";
import { filterOffices, getCierreAlertsBatch, getExtractionFields, listCierrePendingItems } from "../../../ApiCalls/mantisBusiness";
import { pendientesPorOficina } from "../../../utils/cierreCuadreDia";
import {
  columnasDeLista,
  columnasDeResumen,
  montoLegible,
  pasaTarjeta,
  etiquetaDeFaltantes,
  pendientesDeAlertas,
  valorLegible,
  filaDeCierre,
  moneda,
  restarDias,
  fechaDMY,
  ETIQUETA_ESTADO,
} from "../../../utils/cierrePanel";
import { mensajeDeAlerta, nombreDeAlerta } from "../../../utils/cierreNombres";
import { descargarMatriz, hojaDeMatriz } from "../../../utils/cierreExcel";
import { descargarTablas } from "../../../utils/excelTabla";
import { useNombresCierre } from "../../../hooks/useNombresCierre";
import { useAccesoCierres } from "../../../hooks/useAccesoCierres";
import { PERMISO, filasExportables, puede } from "../../../utils/cierrePermisos";
import { EstadoBadge } from "./EstadoBadge";
import { CierreDetailDialog } from "./CierreDetailDialog";
import { CierreManualDialog } from "./CierreManualDialog";
import { EnviarConsolidado } from "./EnviarConsolidado";
import { CuadreDelDia } from "./CuadreDelDia";
import { NavegadorDeDias } from "./NavegadorDeDias";
import { EnviarCorreoOficinas } from "./EnviarCorreoOficinas";
import { NuevoCierre } from "./NuevoCierreDialog";
import { useClienteCierres } from "./ClienteCierresContext";
import { TituloCierres } from "./TituloCierres";
import "./styles/brinksTheme.css";
import { FiltroColumna, FiltroLista, FiltroRangoFechas, FiltroRangoNumero } from "./FiltroColumna";
import { Paginacion } from "./Paginacion";
import { guia } from "../../../utils/cierreTours";

// The "Más" menu's items.
const ITEM_DE_MENU = "flex cursor-default items-center gap-2 rounded-[8px] px-2 py-1.5 text-[13px] font-medium";

// One request's worth of closings (`filterOffices` answers a single page).
const LIMITE_FILAS = 500;

// B-70 — `fechaDelDia` opens the list as one day's detail, from the panel grouped by date
// (`PanelCierresInicio`): the range is fixed on that date and its date filters are not drawn — the
// breadcrumb already says the day, and a range here would take the list off the day it opened.
// Every other filter still works, and `onVolver` returns to the grouped panel. Without it the list
// is exactly what it always was.
function PanelCierres(props) {
  const { fechaDelDia = "", onVolver, onCambiarFecha } = props;
  const diaFijo = !!fechaDelDia;
  // B-55 — which client the shell's selector is on. Every row below is that client's.
  const { clientCode, listo, clientes } = useClienteCierres();
  const [cargando, setCargando] = useState(true);
  const [filasCrudas, setFilasCrudas] = useState([]);
  const [columnas, setColumnas] = useState([]);
  const [columnasResumen, setColumnasResumen] = useState([]);
  // `list: all` — the whole CUADRE, in the wide scrolling table.
  const [ancha, setAncha] = useState(false);
  // The served top-level `export` — how the downloaded Matriz looks (sheet, font, heading colour).
  const [configExcel, setConfigExcel] = useState(null);
  const [alertasPorCierre, setAlertasPorCierre] = useState(null);
  const [busqueda, setBusqueda] = useState("");
  const [estado, setEstado] = useState("todos");
  // B-83 — the summary card the list is narrowed by ("todos" = none). See `pasaTarjeta`.
  const [tarjeta, setTarjeta] = useState("todos");
  const [seleccion, setSeleccion] = useState([]);
  // B-89 — "Enviar correo de oficina(s)": each selected office its own report, at its own addresses.
  const [enviarOficinas, setEnviarOficinas] = useState(false);
  const [detalleClosingId, setDetalleClosingId] = useState(null);
  // B-108 — a closing opened from "Cuadre del día" on another date than the list's: its row.
  const [filaExterna, setFilaExterna] = useState(null);
  // B-108 — the offices with open pending notes: `{ [officeCode]: { cantidad, monto } }`.
  const [notasPorOficina, setNotasPorOficina] = useState({});
  const [pagina, setPagina] = useState(1);
  const [porPagina, setPorPagina] = useState(25);
  const [filtrosAbiertos, setFiltrosAbiertos] = useState(false);
  // B-40 — the manual pipeline: the picker, and the closing being worked by hand (with the
  // opening's own warning, when the office's calendar excluded the date).
  const [manual, setManual] = useState(null);
  const [recarga, setRecarga] = useState(0);
  // B-101 — "Agregar documentos" opens "Nuevo cierre" on this day, for an office's late or corrected files.
  const [pedidoDelDia, setPedidoDelDia] = useState(null);

  // The toolbar's own quick range (Hoy/7 días/Mes + two visible date inputs) — separate from
  // `fFecha` below (the Fecha column's Excel-style popover). brinks keeps both; so do we.
  const [desde, setDesde] = useState(fechaDelDia);
  const [hasta, setHasta] = useState(fechaDelDia);
  const [fFecha, setFFecha] = useState({ desde: "", hasta: "" });
  const [fOficinas, setFOficinas] = useState([]);
  const [fDiferenciaMin, setFDiferenciaMin] = useState("");
  const [fDiferenciaMax, setFDiferenciaMax] = useState("");
  const [fEstados, setFEstados] = useState([]);

  useEffect(() => {
    // B-55 — wait for the client list: a fetch that goes out without a `clientCode` is the 400
    // a business with two cierre clients answers with.
    if (!listo) return undefined;

    let activo = true;
    (async () => {
      setCargando(true);
      setAlertasPorCierre(null);
      // The toolbar's date range goes to the backend, which filters in SQL. Filtering it here over
      // the newest LIMITE_FILAS rows cut a day short as soon as a client had more: with Bancamía's
      // 140 offices a day, 30/09/2026 showed 78 of its 140 closings once four later days existed.
      const filtros = {};
      if (desde) filtros.startDate = desde;
      if (hasta) filtros.endDate = hasta;
      const [response, campos] = await Promise.all([
        filterOffices(props.token, filtros, LIMITE_FILAS, props.mantisBusiness._id, "brinks", clientCode).catch(() => null),
        getExtractionFields(props.token, props.mantisBusiness._id, clientCode).catch(() => null),
      ]);
      if (!activo) return;
      const filas = response?.success ? response.data : [];
      setFilasCrudas(filas);
      setColumnas(columnasDeLista(campos?.fields, campos?.list));
      setColumnasResumen(columnasDeResumen(campos?.fields));
      setAncha(campos?.list === "all");
      setConfigExcel(campos?.export || null);
      setCargando(false);

      // B-62 — every listed closing's alerts in one request (at most 500, what filterOffices
      // returns), so "Bloqueado" and its counter come from the alerts the detail shows.
      const closingIds = [...new Set(filas.map((r) => r.closing_id).filter(Boolean))];
      if (!closingIds.length) {
        setAlertasPorCierre({});
        return;
      }
      const alertas = await getCierreAlertsBatch(props.token, props.mantisBusiness._id, closingIds).catch(() => null);
      if (!activo || !alertas?.success) return;
      setAlertasPorCierre(Object.fromEntries((alertas.data || []).map((e) => [e.closingId, e.alerts || []])));
    })();
    return () => {
      activo = false;
    };
  }, [props.token, props.mantisBusiness._id, clientCode, listo, recarga, desde, hasta]);

  const nombres = useNombresCierre(props.token, props.mantisBusiness._id, clientCode);

  // B-108 — one request for the client's open pending notes, to mark their offices in the list. A
  // client without a day reconciliation answers 404, and nothing is marked.
  useEffect(() => {
    if (!listo || !clientCode) return undefined;
    let activo = true;
    listCierrePendingItems(props.token, props.mantisBusiness._id, clientCode, { status: "OPEN" })
      .then((r) => {
        if (activo) setNotasPorOficina(r?.success ? pendientesPorOficina(r.data?.items) : {});
      })
      .catch(() => {
        if (activo) setNotasPorOficina({});
      });
    return () => {
      activo = false;
    };
  }, [props.token, props.mantisBusiness._id, clientCode, listo, recarga]);

  // BR-38 — what this person may do from the list. Opening a closing is offered to whoever can
  // open one somewhere or run a client's day (both live behind "Nuevo cierre"); the dialog then
  // offers only the offices they may open. Export is asked of this client, and writes only the
  // rows whose office it reaches (`filasExportables`).
  const acceso = useAccesoCierres();
  const puedeAbrir = puede(acceso, PERMISO.CREAR_CIERRES) || puede(acceso, PERMISO.PROCESAR_DIA);
  const puedeCompletarDia = !!fechaDelDia && puede(acceso, PERMISO.PROCESAR_DIA, { cliente: clientCode, entero: true });
  // Redoing the day reopens and discards on every office: reopening's permission too (B-80).
  const puedeRehacerDia = puedeCompletarDia && puede(acceso, PERMISO.REABRIR, { cliente: clientCode, entero: true });
  const puedeExportar = puede(acceso, PERMISO.EXPORTAR, { cliente: clientCode });
  // B-71 — the day's consolidated email carries every office's Matriz, so it needs the whole client.
  const puedeEnviarConsolidado = diaFijo && puede(acceso, PERMISO.EXPORTAR, { cliente: clientCode, entero: true });
  // B-101 — the day's account sums every office, so it needs the closings' view on the whole client.
  const puedeVerCuadre = diaFijo && puede(acceso, PERMISO.VER_CIERRES, { cliente: clientCode, entero: true });

  const todasLasFilas = useMemo(
    () =>
      filasCrudas.map((row) =>
        filaDeCierre(row, columnas, columnasResumen, alertasPorCierre, (clave) => nombreDeAlerta(nombres, clave))
      ),
    [filasCrudas, columnas, columnasResumen, alertasPorCierre, nombres]
  );

  // B-108 — "Cuadre del día" opens an office's closing: the list's own row when it holds that
  // office on that date, else that one row asked of the server (the day on screen may be another).
  const verOficina = async (codigo, fecha) => {
    const propia = todasLasFilas.find((f) => String(f.codigo) === String(codigo) && f.fecha === fecha && f.closingId);
    if (propia) {
      setDetalleClosingId(propia.closingId);
      return;
    }
    const r = await filterOffices(
      props.token, { startDate: fecha, endDate: fecha, office: codigo }, 100, props.mantisBusiness._id, "brinks", clientCode
    ).catch(() => null);
    const fila = (r?.success ? r.data || [] : []).find((row) => String(row.codigo_oficina) === String(codigo) && row.closing_id);
    if (fila) {
      setFilaExterna(fila);
      setDetalleClosingId(fila.closing_id);
    }
  };

  // "hoy" (for the Hoy/7 días/Mes shortcuts) is the most recent closing's date. Unlike brinks'
  // static mock data, this is real and moves: the legacy offices (3091/4956, still registered —
  // see reference/clients/davivienda.yaml) get a fresh SCHEDULED closing every day from the real
  // backend cron, so this resolves to today far more often than to 2026-08-18, the pilot
  // offices' actual evidence date. Deliberately NOT defaulting `desde`/`hasta` to "last 7 days"
  // from it the way brinks does: with today's date as the anchor, that default would silently
  // filter every real Davivienda closing out of view on load, which is the opposite of what
  // this page is for. The shortcuts stay available; nothing is pre-applied.
  const hoy = useMemo(() => {
    const fechas = todasLasFilas.map((f) => f.fecha).filter(Boolean).sort();
    return fechas[fechas.length - 1] || new Date().toISOString().slice(0, 10);
  }, [todasLasFilas]);

  // Every filter but the cards' own — what the cards count, so choosing "Bloqueadas" does not
  // turn the other cards to 0.
  const filtradasSinTarjeta = useMemo(
    () =>
      todasLasFilas.filter((f) => {
        if (desde && f.fecha < desde) return false;
        if (hasta && f.fecha > hasta) return false;
        if (busqueda && !`${f.oficina} ${f.codigo} ${f.regional || ""}`.toLowerCase().includes(busqueda.toLowerCase())) return false;
        if (estado !== "todos" && f.estado !== estado) return false;
        if (fFecha.desde && f.fecha < fFecha.desde) return false;
        if (fFecha.hasta && f.fecha > fFecha.hasta) return false;
        if (fOficinas.length && !fOficinas.includes(f.oficinaId)) return false;
        if (fDiferenciaMin !== "" && (f.diferencia == null || f.diferencia < Number(fDiferenciaMin))) return false;
        if (fDiferenciaMax !== "" && (f.diferencia == null || f.diferencia > Number(fDiferenciaMax))) return false;
        if (fEstados.length && !fEstados.includes(f.estado)) return false;
        return true;
      }),
    [todasLasFilas, desde, hasta, busqueda, estado, fFecha, fOficinas, fDiferenciaMin, fDiferenciaMax, fEstados]
  );

  const filtradas = useMemo(
    () => filtradasSinTarjeta.filter((f) => pasaTarjeta(f, tarjeta)),
    [filtradasSinTarjeta, tarjeta]
  );

  const resumen = useMemo(
    () => ({
      oficinas: new Set(filtradasSinTarjeta.map((f) => f.oficinaId)).size,
      cuadre: filtradasSinTarjeta.filter((f) => f.estado === "cuadre").length,
      pendiente: filtradasSinTarjeta.filter((f) => f.estado === "pendiente").length,
      bloqueado: filtradasSinTarjeta.filter((f) => f.estado === "bloqueado").length,
      discrepancia: filtradasSinTarjeta.reduce((a, f) => a + (f.discrepancia || 0), 0),
    }),
    [filtradasSinTarjeta]
  );

  const filtrosActivos = [
    !!(desde || hasta) && !(diaFijo && desde === fechaDelDia && hasta === fechaDelDia),
    !!busqueda,
    estado !== "todos",
    tarjeta !== "todos",
    !!(fFecha.desde || fFecha.hasta),
    fOficinas.length > 0,
    fDiferenciaMin !== "" || fDiferenciaMax !== "",
    fEstados.length > 0,
  ].filter(Boolean).length;

  const totalPaginas = Math.max(1, Math.ceil(filtradas.length / porPagina));
  const paginaActual = Math.min(pagina, totalPaginas);
  const inicio = (paginaActual - 1) * porPagina;
  const visibles = useMemo(() => filtradas.slice(inicio, inicio + porPagina), [filtradas, inicio, porPagina]);

  useEffect(() => {
    setPagina(1);
  }, [filtradas.length, porPagina]);

  // The toolbar's own shortcuts (desde/hasta) — what the Hoy/7 días/Mes buttons next to the
  // visible date inputs control.
  const rango = (dias) => {
    setDesde(restarDias(hoy, dias));
    setHasta(hoy);
  };
  const rangoActivo = (dias) => desde === restarDias(hoy, dias) && hasta === hoy;

  // The Fecha column's own popover shortcuts — a second, independent set over `fFecha`,
  // exactly as brinks has both.
  const rangoColumna = (dias) => setFFecha({ desde: restarDias(hoy, dias), hasta: hoy });
  const rangoColumnaActivo = (dias) => fFecha.desde === restarDias(hoy, dias) && fFecha.hasta === hoy;

  const limpiarFiltrosColumna = () => {
    setFFecha({ desde: "", hasta: "" });
    setFOficinas([]);
    setFDiferenciaMin("");
    setFDiferenciaMax("");
    setFEstados([]);
  };

  const restablecer = () => {
    setDesde(fechaDelDia);
    setHasta(fechaDelDia);
    setBusqueda("");
    setEstado("todos");
    setTarjeta("todos");
    setSeleccion([]);
    limpiarFiltrosColumna();
  };

  const opcionesOficinas = useMemo(
    () =>
      [...new Map(todasLasFilas.map((f) => [f.oficinaId, { valor: f.oficinaId, etiqueta: `${f.codigo} · ${f.oficina}` }])).values()].sort(
        (a, b) => a.etiqueta.localeCompare(b.etiqueta)
      ),
    [todasLasFilas]
  );

  // Toolbar shortcuts (desde/hasta) — the visible Hoy/7 días/Mes buttons next to the date inputs.
  const atajosFechaToolbar = [
    { etiqueta: "Todo", activo: !desde && !hasta, onClick: () => { setDesde(fechaDelDia); setHasta(fechaDelDia); } },
    { etiqueta: "Hoy", activo: desde === hoy && hasta === hoy, onClick: () => { setDesde(hoy); setHasta(hoy); } },
    { etiqueta: "7 días", activo: rangoActivo(6), onClick: () => rango(6) },
    { etiqueta: "Mes", activo: rangoActivo(29), onClick: () => rango(29) },
  ];

  // Fecha column popover shortcuts (fFecha) — a second, independent set, same as brinks.
  const atajosFecha = [
    { etiqueta: "Hoy", activo: fFecha.desde === hoy && fFecha.hasta === hoy, onClick: () => setFFecha({ desde: hoy, hasta: hoy }) },
    { etiqueta: "Últimos 7 días", activo: rangoColumnaActivo(6), onClick: () => rangoColumna(6) },
    { etiqueta: "Este mes", activo: rangoColumnaActivo(29), onClick: () => rangoColumna(29) },
  ];

  const atajosDinero = (set, min, max) => [
    { etiqueta: "Negativos", activo: min === "" && max === "-1", onClick: () => set("", "-1") },
    { etiqueta: "Cero", activo: min === "0" && max === "0", onClick: () => set("0", "0") },
    { etiqueta: "Positivos", activo: min === "1" && max === "", onClick: () => set("1", "") },
  ];

  const toggle = (id) => setSeleccion((s) => (s.includes(id) ? s.filter((x) => x !== id) : [...s, id]));
  const todosSeleccionados = visibles.length > 0 && visibles.every((f) => seleccion.includes(f.id));

  const exportar = (todas) => {
    const filas = filasExportables(acceso, clientCode, todas, (f) => ({ oficina: f.raw.codigo_oficina, region: f.regional }));
    // The whole CUADRE downloads as the client's own workbook (the Matriz), not as a CSV.
    if (ancha) {
      descargarMatriz(hojaDeMatriz({ columnas, filas, config: configExcel })).catch((e) => {
        // The click is the only feedback surface; a failed download is logged, not swallowed.
        console.error("No se pudo generar el Excel", e);
      });
      return;
    }
    // Every other client: the list as an Excel table — a title, the headings, pesos in their format, a
    // filter and the totals — never a file of comma-separated values.
    const cuerpo = filas.map((f) => {
      // Missing documents only: a closing that does not balance is not missing anything.
      const n = f.closingId ? pendientesDeAlertas(alertasPorCierre?.[f.closingId])?.faltantes ?? null : null;
      return [
        f.fechaLabel,
        f.codigo,
        f.oficina,
        n == null ? null : n,
        ...f.valores.map((v) => (v === null || v === undefined || v === "" ? null : Number(v))),
        ETIQUETA_ESTADO[f.estado],
      ];
    });
    const totales = [
      "", "", "Total", cuerpo.reduce((t, fila) => t + (Number(fila[3]) || 0), 0),
      ...columnas.map((c, i) => (c.formato === "count" || c.formato === "money" || !c.formato
        ? cuerpo.reduce((t, fila) => t + (Number(fila[4 + i]) || 0), 0)
        : "")),
      "",
    ];
    const periodo = fechaDelDia ? fechaDMY(fechaDelDia) : `${cuerpo.length} cierres`;
    Promise.resolve(descargarTablas(`Cierres ${clientCode || ""} ${fechaDelDia ? fechaDMY(fechaDelDia) : ""}.xlsx`.replace(/\s+\.xlsx$/, ".xlsx"), [{
      nombre: "Cierres",
      titulo: `Panel de cierres · ${clientCode || ""}`,
      subtitulo: periodo,
      columnas: [
        { titulo: "Fecha", ancho: 12 },
        { titulo: "Código", ancho: 9 },
        { titulo: "Oficina", ancho: 26 },
        { titulo: "Documentos faltantes", formato: "count", ancho: 13 },
        ...columnas.map((c) => ({ titulo: c.etiqueta, formato: c.formato === "count" ? "count" : "money", ancho: 18 })),
        { titulo: "Estado", ancho: 18 },
      ],
      filas: cuerpo,
      totales,
    }])).catch((e) => {
      console.error("No se pudo generar el Excel", e);
    });
  };

  const copiarResumen = async () => {
    const texto = `Panel de cierres — ${resumen.oficinas} oficinas · ${resumen.cuadre} en cuadre · ${resumen.pendiente} pendientes · ${resumen.bloqueado} bloqueadas · Diferencia total ${moneda(resumen.discrepancia)}`;
    try {
      await navigator.clipboard.writeText(texto);
    } catch (e) {
      // A clipboard write can be refused (permissions, insecure context) — nothing to recover
      // to, and the button itself is the only feedback surface this mockup ever had.
    }
  };

  // The differences, the only cells drawn red when non-zero — in the whole CUADRE a non-zero
  // ingreso is the normal case, not a warning.
  const esDiferencia = (fieldName) => columnasResumen.some((c) => c.fieldName === fieldName);

  // `list: all` pins the checkbox, Fecha and Oficina while the CUADRE scrolls under them. Fixed
  // widths, because each pinned column's `left` is the sum of the ones before it.
  const fija = (left) => (ancha ? cn("sticky z-10 bg-card group-hover:bg-brinks-surface-hover", left) : null);
  const fijaCabecera = (left) => (ancha ? cn("sticky top-0 z-30", left) : null);

  const tarjetas = [
    { filtro: "todos", icono: Building2, etiqueta: "Total oficinas", valor: String(resumen.oficinas), tono: "bg-brinks-surface text-brinks-foreground" },
    { filtro: "cuadre", icono: Check, etiqueta: "Cuadre OK", valor: String(resumen.cuadre), tono: "bg-brinks-success-soft text-brinks-success" },
    { filtro: "pendiente", icono: AlertTriangle, etiqueta: "Pendiente revisión", valor: String(resumen.pendiente), tono: "bg-brinks-warning-soft text-brinks-warning" },
    { filtro: "bloqueado", icono: Shield, etiqueta: "Bloqueadas", valor: String(resumen.bloqueado), tono: "bg-brinks-danger-soft text-brinks-danger" },
    { filtro: "diferencia", icono: Coins, etiqueta: "Discrepancia total", valor: montoLegible(resumen.discrepancia), tono: "bg-brinks-surface text-brinks-foreground" },
  ];

  return (
    <div className="brinks-scope font-redhat flex min-h-0 flex-col gap-2 bg-background p-3 sm:p-4">
      <header className="mb-1 flex flex-wrap items-end justify-between gap-2">
        <div>
          {diaFijo ? (
            <nav aria-label="Ruta" className="mb-1 flex items-center gap-1.5 text-[12px] text-brinks-muted-foreground">
              <button type="button" onClick={onVolver} className="inline-flex items-center gap-1 transition hover:text-brinks-foreground">
                <ArrowLeft className="h-3.5 w-3.5" /> Cierres por fecha
              </button>
              <span aria-hidden="true">/</span>
              {onCambiarFecha ? (
                <NavegadorDeDias
                  token={props.token}
                  mantisBusinessId={props.mantisBusiness._id}
                  clientCode={clientCode}
                  fecha={fechaDelDia}
                  onCambiarFecha={onCambiarFecha}
                />
              ) : (
                <span className="tabular-nums text-brinks-foreground">{fechaDMY(fechaDelDia)}</span>
              )}
            </nav>
          ) : null}
          <TituloCierres />
          <p className="mt-0.5 text-[13px] text-brinks-muted-foreground">
            Centro de control, cuadre de caja y conciliación diaria de oficinas
          </p>
          {/* The backend answers one page; at the limit some closings are not in it, and the totals would be short. */}
          {filasCrudas.length >= LIMITE_FILAS ? (
            <p className="mt-1 text-[12px] text-brinks-warning">
              Se muestran los {LIMITE_FILAS} cierres más recientes. Elige un rango de fechas para ver todos los de ese período.
            </p>
          ) : null}
        </div>
        {/* What a person does every day is in view — the day's cuadre, an office's late documents,
            the export (the highlighted one) and opening a closing; what is seldom done, or discards
            work (redoing the day), waits in "Más". Each action keeps its handler and its permission. */}
        <div {...guia("Acciones del día", "El cuadre del día, el envío del consolidado al cliente y la exportación del informe.")} className="flex flex-wrap items-center gap-2">
          {puedeVerCuadre ? (
            <CuadreDelDia
              token={props.token}
              mantisBusinessId={props.mantisBusiness._id}
              clientCode={clientCode}
              clienteNombre={(clientes || []).find((c) => c?.code === clientCode)?.name}
              fecha={fechaDelDia}
              onVerOficina={verOficina}
              onCambiarFecha={onCambiarFecha}
            />
          ) : null}
          {puedeEnviarConsolidado ? (
            <EnviarConsolidado
              token={props.token}
              mantisBusinessId={props.mantisBusiness._id}
              clientCode={clientCode}
              clienteNombre={(clientes || []).find((c) => c?.code === clientCode)?.name}
              fecha={fechaDelDia}
            />
          ) : null}
          {puedeExportar ? (
            <Button size="sm" className="h-9 gap-1.5" onClick={() => exportar(filtradas)}>
              <Download className="h-4 w-4 shrink-0" /> <span className="truncate">Exportar informe</span>
            </Button>
          ) : null}
          <div className="flex items-center gap-2 sm:ml-1 sm:border-l sm:pl-3">
            {puedeAbrir ? (
              <NuevoCierre
                token={props.token}
                mantisBusinessId={props.mantisBusiness._id}
                clientCode={clientCode}
                pedido={pedidoDelDia}
                onOpened={(abierto) => {
                  setManual({ closingId: abierto.closingId, aviso: abierto.warning?.message || null, region: abierto.region });
                  if (abierto.created) setRecarga((n) => n + 1);
                }}
                onDiaProcesado={() => setRecarga((n) => n + 1)}
              />
            ) : null}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="outline" size="sm" className="h-9 w-9 px-0" aria-label="Más acciones" title="Más acciones">
                  <MoreHorizontal className="h-4 w-4" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="brinks-scope font-redhat w-60 rounded-[12px] p-1">
                <DropdownMenuItem onSelect={copiarResumen} className={ITEM_DE_MENU}>
                  <Copy className="h-4 w-4" /> Copiar resumen
                </DropdownMenuItem>
                {/* B-101 — the offices share the day's reports, so the day is redone here, with all of
                    them; set apart, since it discards what people did on every office. */}
                {puedeRehacerDia && filasCrudas.length ? (
                  <>
                    <DropdownMenuSeparator />
                    <DropdownMenuItem
                      onSelect={() => setPedidoDelDia({ fecha: fechaDelDia, clientCode })}
                      className={cn(ITEM_DE_MENU, "text-brinks-danger")}
                    >
                      <RotateCcw className="h-4 w-4" /> Rehacer el día
                    </DropdownMenuItem>
                  </>
                ) : null}
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>
      </header>

      <section {...guia("Resumen", "Cuántas oficinas cuadran, cuántas faltan por revisar y cuáles están bloqueadas. Toca una tarjeta para filtrar la lista.")} className="grid grid-cols-2 gap-1.5 sm:grid-cols-3 sm:gap-2 xl:grid-cols-5">
        {/* B-83 — each card is also a filter: a click narrows the list to it, a second click (or
            "Total oficinas") clears it. The counts ignore the card's own filter. */}
        {tarjetas.map((t) => (
          <button
            type="button"
            key={t.etiqueta}
            aria-pressed={tarjeta === t.filtro && t.filtro !== "todos"}
            title={t.filtro === "todos" ? "Ver todas" : `Ver solo: ${t.etiqueta}`}
            onClick={() => setTarjeta((actual) => (actual === t.filtro || t.filtro === "todos" ? "todos" : t.filtro))}
            className={cn(
              "min-w-0 rounded-[10px] border bg-card px-2 py-1.5 text-left shadow-brinks-card transition hover:border-brinks-border-strong sm:px-2.5",
              tarjeta === t.filtro && t.filtro !== "todos" && "border-brinks-foreground ring-1 ring-brinks-foreground"
            )}
          >
            <div className="flex items-center gap-2">
              <span className={cn("grid h-7 w-7 shrink-0 place-items-center rounded-[8px] sm:h-8 sm:w-8", t.tono)}>
                <t.icono className="h-[15px] w-[15px]" />
              </span>
              <div className="min-w-0">
                <p className="truncate text-[15px] font-semibold leading-tight tabular-nums sm:text-[16px] xl:text-[18px]">
                  {t.valor}
                </p>
                <p className="truncate text-[10.5px] leading-tight text-brinks-muted-foreground sm:text-[11px]">
                  {t.etiqueta}
                </p>
              </div>
            </div>
          </button>
        ))}
      </section>

      <section {...guia("Filtros", "Elige el periodo (Todo, Hoy, 7 días, Mes o un rango de fechas). Dentro de un día también buscas una oficina y filtras por estado.")} className="rounded-[10px] border bg-card p-1.5 shadow-brinks-card">
        <div className="grid items-center md:hidden">
          <Button
            size="sm"
            variant="outline"
            className="h-8 min-w-0 justify-between gap-1 px-2 text-[12px]"
            onClick={() => setFiltrosAbiertos((v) => !v)}
            aria-expanded={filtrosAbiertos}
          >
            <span className="flex items-center gap-1.5">
              <Filter className="h-3.5 w-3.5" /> Filtros
              {filtrosActivos > 0 ? (
                <span className="rounded-full bg-primary px-1.5 text-[10px] font-semibold text-primary-foreground tabular-nums">
                  {filtrosActivos}
                </span>
              ) : null}
            </span>
            <ChevronDown className={cn("h-3.5 w-3.5 transition", filtrosAbiertos && "rotate-180")} />
          </Button>
        </div>

        {filtrosAbiertos ? (
          <div className="mt-1.5 grid gap-1.5 md:hidden">
            {diaFijo ? null : (
              <>
                <div className="grid grid-cols-4 gap-1.5">
                  {atajosFechaToolbar.map((a) => (
                    <Button key={a.etiqueta} size="sm" className="h-10 w-full px-1 text-[13px]" variant={a.activo ? "default" : "outline"} onClick={a.onClick}>
                      {a.etiqueta}
                    </Button>
                  ))}
                </div>
                <div className="grid grid-cols-2 gap-1.5">
                  <input type="date" value={desde} onChange={(e) => setDesde(e.target.value)} className="h-10 min-w-0 w-full rounded-[7px] border bg-card px-2 text-[13px] outline-none transition focus:border-brinks-border-strong" aria-label="Fecha inicial" />
                  <input type="date" value={hasta} onChange={(e) => setHasta(e.target.value)} className="h-10 min-w-0 w-full rounded-[7px] border bg-card px-2 text-[13px] outline-none transition focus:border-brinks-border-strong" aria-label="Fecha final" />
                </div>
              </>
            )}
            <div className="grid grid-cols-2 gap-1.5">
              <span className="relative block min-w-0">
                <Search className="pointer-events-none absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-brinks-muted-foreground" />
                <input
                  value={busqueda}
                  onChange={(e) => setBusqueda(e.target.value)}
                  placeholder="Nombre, código o regional..."
                  className="h-10 w-full rounded-[7px] border bg-card pl-8 pr-2 text-[13px] outline-none focus:border-brinks-border-strong"
                />
              </span>
              <Select value={estado} onValueChange={setEstado}>
                <SelectTrigger className="h-10 min-w-0 w-full rounded-[7px] text-[13px]" aria-label="Estado">
                  <SelectValue>{estado === "todos" ? "Todos los estados" : ETIQUETA_ESTADO[estado]}</SelectValue>
                </SelectTrigger>
                <SelectContent className="brinks-scope font-redhat">
                  <SelectItem value="todos">Todos los estados</SelectItem>
                  <SelectItem value="cuadre">Cuadre</SelectItem>
                  <SelectItem value="pendiente">Pendiente</SelectItem>
                  <SelectItem value="bloqueado">Bloqueado</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <Button size="sm" variant="ghost" className="h-10 justify-self-end gap-1.5 px-3 text-[13px]" onClick={restablecer}>
              <RotateCcw className="h-4 w-4" /> Restablecer
            </Button>
          </div>
        ) : null}

        <div className="hidden min-w-0 grid-cols-[minmax(0,1fr)_auto] items-center gap-1 md:grid xl:gap-1.5">
          <div
            className={cn(
              "grid min-w-0 items-center gap-1 xl:gap-1.5",
              diaFijo
                ? "grid-cols-[minmax(0,1fr)_200px] lg:grid-cols-[minmax(0,1fr)_220px]"
                : "grid-cols-[auto_minmax(0,1fr)_minmax(0,1fr)_minmax(0,1.4fr)_minmax(0,1fr)] xl:grid-cols-[auto_136px_136px_minmax(0,320px)_200px]"
            )}
          >
            {diaFijo ? null : (
              <>
                <div className="grid shrink-0 grid-cols-4 items-center gap-0.5 xl:gap-1">
                  {atajosFechaToolbar.map((a) => (
                    <Button key={a.etiqueta} size="sm" className="h-9 min-w-0 px-1.5 text-[12px] xl:px-2.5 xl:text-[13px]" variant={a.activo ? "default" : "outline"} onClick={a.onClick}>
                      {a.etiqueta}
                    </Button>
                  ))}
                </div>
                <input type="date" value={desde} onChange={(e) => setDesde(e.target.value)} className="h-9 min-w-0 w-full rounded-[7px] border bg-card px-2 text-[12px] outline-none transition focus:border-brinks-border-strong xl:text-[13px]" aria-label="Fecha inicial" />
                <input type="date" value={hasta} onChange={(e) => setHasta(e.target.value)} className="h-9 min-w-0 w-full rounded-[7px] border bg-card px-2 text-[12px] outline-none transition focus:border-brinks-border-strong xl:text-[13px]" aria-label="Fecha final" />
              </>
            )}
            <span className="relative block min-w-0">
              <Search className="pointer-events-none absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-brinks-muted-foreground" />
              <input
                value={busqueda}
                onChange={(e) => setBusqueda(e.target.value)}
                placeholder="Nombre, código o regional..."
                className="h-9 min-w-0 w-full rounded-[7px] border bg-card pl-8 pr-2 text-[12px] outline-none focus:border-brinks-border-strong xl:text-[13px]"
              />
            </span>
            <Select value={estado} onValueChange={setEstado}>
              <SelectTrigger className="h-9 min-w-0 w-full rounded-[7px] px-3 text-[12px] xl:text-[13px]" aria-label="Estado">
                <SelectValue>{estado === "todos" ? "Todos los estados" : ETIQUETA_ESTADO[estado]}</SelectValue>
              </SelectTrigger>
              <SelectContent className="brinks-scope font-redhat">
                <SelectItem value="todos">Todos los estados</SelectItem>
                <SelectItem value="cuadre">Cuadre</SelectItem>
                <SelectItem value="pendiente">Pendiente</SelectItem>
                <SelectItem value="bloqueado">Bloqueado</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="flex shrink-0 items-center gap-1 border-l pl-1 xl:gap-1.5 xl:pl-1.5">
            <Button size="sm" variant="ghost" className="h-9 w-9 gap-1.5 px-0 text-[13px] xl:w-auto xl:px-3" onClick={restablecer}>
              <RotateCcw className="h-4 w-4" /> <span className="hidden xl:inline">Restablecer</span>
            </Button>
          </div>
        </div>
      </section>

      {cargando ? (
        <div className="grid place-items-center rounded-[10px] border border-dashed bg-card py-14">
          <Spinner size="lg" />
        </div>
      ) : (
        <>
          <section {...guia("Lista", "Toca una fila para abrirla: un día muestra sus oficinas y una oficina muestra su cierre con documentos, cifras y alertas.")} className="hidden min-w-0 flex-col overflow-hidden rounded-[10px] border bg-card shadow-brinks-card md:flex">
            <div className={cn("min-w-0 overflow-y-auto bg-card", ancha ? "overflow-x-auto" : "overflow-x-hidden")}>
              <table className={cn("table-fixed text-[12px] xl:text-[13px]", ancha ? "w-max min-w-full" : "w-full")}>
                {ancha ? (
                  <colgroup>
                    <col className="w-[40px]" />
                    <col className="w-[96px]" />
                    <col className="w-[220px]" />
                    <col className="w-[120px]" />
                    <col className="w-[130px]" />
                    {columnas.map((c) => (
                      <col key={c.fieldName} className={c.formato === "count" ? "w-[124px]" : "w-[150px]"} />
                    ))}
                    <col className="w-[260px]" />
                    <col className="w-[150px]" />
                  </colgroup>
                ) : (
                  <colgroup>
                    <col className="w-[5%]" />
                    <col className="w-[12%]" />
                    <col className={columnas.length ? "w-[22%]" : "w-[56%]"} />
                    <col className="w-[13%]" />
                    {/* B-62 — the client's resumen columns share what Cuadre total/DOE/Conciliación had. */}
                    {columnas.map((c) => (
                      <col key={c.fieldName} style={{ width: `${(34 / columnas.length).toFixed(2)}%` }} />
                    ))}
                    <col className="w-[13%]" />
                  </colgroup>
                )}
                <thead className="sticky top-0 z-20 bg-brinks-surface">
                  <tr className="border-b bg-brinks-surface text-left text-[10.5px] uppercase tracking-[0.04em] text-brinks-muted-foreground">
                    <th className={cn("bg-brinks-surface px-2 py-2 xl:px-3", fijaCabecera("left-0"))}>
                      <Checkbox
                        checked={todosSeleccionados}
                        onCheckedChange={(v) =>
                          setSeleccion((s) => (v ? [...new Set([...s, ...visibles.map((f) => f.id)])] : s.filter((id) => !visibles.some((f) => f.id === id))))
                        }
                        aria-label="Seleccionar todo"
                      />
                    </th>
                    <th className={cn("min-w-0 bg-brinks-surface px-2 py-2 font-semibold xl:px-3", fijaCabecera("left-[40px]"))}>
                      <span className="flex min-w-0 items-center justify-between gap-1">
                        <span className="min-w-0 leading-tight">Fecha</span>
                        {diaFijo ? null : (
                          <FiltroColumna titulo="fecha" activo={!!(fFecha.desde || fFecha.hasta)} onLimpiar={() => setFFecha({ desde: "", hasta: "" })}>
                            <FiltroRangoFechas desde={fFecha.desde} hasta={fFecha.hasta} onChange={(d, h) => setFFecha({ desde: d, hasta: h })} atajos={atajosFecha} />
                          </FiltroColumna>
                        )}
                      </span>
                    </th>
                    <th className={cn("min-w-0 bg-brinks-surface px-2 py-2 font-semibold xl:px-3", fijaCabecera("left-[136px]"))}>
                      <span className="flex min-w-0 items-center justify-between gap-1">
                        <span className="min-w-0 leading-tight">Oficina</span>
                        <FiltroColumna titulo="oficina" activo={fOficinas.length > 0} onLimpiar={() => setFOficinas([])}>
                          <FiltroLista buscable opciones={opcionesOficinas} seleccionados={fOficinas} onChange={setFOficinas} />
                        </FiltroColumna>
                      </span>
                    </th>
                    {ancha ? (
                      <th className="min-w-0 bg-brinks-surface px-2 py-2 font-semibold xl:px-3">
                        <span className="min-w-0 leading-tight">Regional (C)</span>
                      </th>
                    ) : null}
                    <th className="min-w-0 bg-brinks-surface px-2 py-2 font-semibold xl:px-3">
                      <span className="min-w-0 leading-tight">Pendientes</span>
                    </th>
                    {columnas.map((c) => (
                      <th key={c.fieldName} className="min-w-0 bg-brinks-surface px-2 py-2 text-center align-middle font-semibold xl:px-3">
                        {/* B-83 — centred like the figures under it; one line when it fits, two at most. */}
                        <span className="flex min-w-0 items-center justify-center gap-1">
                          <span className="line-clamp-2 min-w-0 break-words leading-tight" title={c.etiqueta}>{c.etiqueta}</span>
                          {/* The range filter belongs to the first difference — the first column for
                              Davivienda, AA for Bancamía's CUADRE. */}
                          {c.fieldName === columnasResumen[0]?.fieldName ? (
                            <FiltroColumna
                              titulo={c.etiqueta.toLowerCase()}
                              activo={fDiferenciaMin !== "" || fDiferenciaMax !== ""}
                              onLimpiar={() => {
                                setFDiferenciaMin("");
                                setFDiferenciaMax("");
                              }}
                              align="end"
                            >
                              <FiltroRangoNumero
                                min={fDiferenciaMin}
                                max={fDiferenciaMax}
                                onChange={(m, x) => {
                                  setFDiferenciaMin(m);
                                  setFDiferenciaMax(x);
                                }}
                                atajos={atajosDinero((m, x) => { setFDiferenciaMin(m); setFDiferenciaMax(x); }, fDiferenciaMin, fDiferenciaMax)}
                              />
                            </FiltroColumna>
                          ) : null}
                        </span>
                      </th>
                    ))}
                    {ancha ? (
                      <th className="min-w-0 bg-brinks-surface px-2 py-2 font-semibold xl:px-3">
                        <span className="min-w-0 leading-tight">Observaciones (AK)</span>
                      </th>
                    ) : null}
                    <th className="min-w-0 bg-brinks-surface px-2 py-2 font-semibold xl:px-3">
                      <span className="flex min-w-0 items-center justify-between gap-1">
                        <span className="min-w-0 leading-tight">Estado</span>
                        <FiltroColumna titulo="estado" activo={fEstados.length > 0} onLimpiar={() => setFEstados([])} align="end">
                          <FiltroLista
                            opciones={[
                              { valor: "cuadre", etiqueta: "Cuadre" },
                              { valor: "pendiente", etiqueta: "Pendiente revisión" },
                              { valor: "bloqueado", etiqueta: "Bloqueado" },
                            ]}
                            seleccionados={fEstados}
                            onChange={setFEstados}
                          />
                        </FiltroColumna>
                      </span>
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {visibles.map((f) => {
                    const activa = seleccion.includes(f.id);
                    return (
                      <tr
                        key={f.id}
                        onClick={() => f.closingId && setDetalleClosingId(f.closingId)}
                        className={cn("group h-[40px] cursor-pointer border-b transition last:border-0 hover:bg-brinks-surface-hover", activa && "bg-brinks-surface")}
                      >
                        <td className={cn("px-2 xl:px-3", fija("left-0"), ancha && activa && "bg-brinks-surface")} onClick={(e) => e.stopPropagation()}>
                          <Checkbox checked={activa} onCheckedChange={() => toggle(f.id)} aria-label={`Seleccionar ${f.oficina}`} />
                        </td>
                        <td className={cn("whitespace-nowrap px-2 tabular-nums text-brinks-muted-foreground xl:px-3", fija("left-[40px]"), ancha && activa && "bg-brinks-surface")}>
                          {f.fechaLabel}
                        </td>
                        <td className={cn("min-w-0 px-2 xl:px-3", fija("left-[136px]"), ancha && activa && "bg-brinks-surface")}>
                          <span className="flex min-w-0 items-center gap-1.5">
                            <span className="shrink-0 rounded-[5px] border bg-brinks-surface px-1.5 py-0.5 text-[10.5px] font-semibold text-brinks-muted-foreground">
                              {f.codigo}
                            </span>
                            <span className="min-w-0 truncate" title={f.oficina}>{f.oficina}</span>
                            <MarcaNotas notas={notasPorOficina[String(f.codigo)]} />
                          </span>
                        </td>
                        {ancha ? (
                          <td className="truncate whitespace-nowrap px-2 text-brinks-muted-foreground xl:px-3" title={f.regional || undefined}>
                            {f.regional || "—"}
                          </td>
                        ) : null}
                        <td className="min-w-0 px-2 xl:px-3">
                          <CeldaFaltantes alertas={alertasPorCierre ? alertasPorCierre[f.closingId] || [] : undefined} tieneClosing={!!f.closingId} nombres={nombres} />
                        </td>
                        {f.valores.map((v, i) => (
                          <td
                            key={columnas[i].fieldName}
                            className={cn(
                              "truncate whitespace-nowrap px-2 text-center tabular-nums xl:px-3",
                              v != null && v !== 0 && esDiferencia(columnas[i].fieldName) && "font-semibold text-brinks-danger"
                            )}
                          >
                            {valorLegible(v, columnas[i].formato)}
                          </td>
                        ))}
                        {ancha ? (
                          <td className="px-2 text-brinks-muted-foreground xl:px-3" title={f.observaciones || undefined}>
                            <span className="line-clamp-2 whitespace-normal break-words leading-snug">
                              {f.observaciones || "—"}
                            </span>
                          </td>
                        ) : null}
                        <td className="min-w-0 px-2 xl:px-3">
                          <EstadoBadge estado={f.estado} />
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
              {filtradas.length === 0 && (
                <p className="p-8 text-center text-sm text-brinks-muted-foreground">No hay cierres para los filtros seleccionados.</p>
              )}
            </div>
            {filtradas.length > 0 ? (
              <Paginacion
                inicio={inicio}
                porPagina={porPagina}
                total={filtradas.length}
                paginaActual={paginaActual}
                totalPaginas={totalPaginas}
                onPorPagina={setPorPagina}
                onPagina={setPagina}
              />
            ) : null}
          </section>

          <section {...guia("Lista", "Toca una fila para abrirla: un día muestra sus oficinas y una oficina muestra su cierre con documentos, cifras y alertas.")} className="space-y-2 md:hidden">
            {visibles.map((f) => (
              <article key={f.id} onClick={() => f.closingId && setDetalleClosingId(f.closingId)} className="rounded-[12px] border bg-card p-3 shadow-brinks-card">
                <div className="grid grid-cols-[minmax(0,1fr)_auto] items-start gap-3">
                  <div className="min-w-0">
                    <p className="flex min-w-0 items-center gap-1.5 font-semibold">
                      <span className="truncate">{f.oficina}</span>
                      <MarcaNotas notas={notasPorOficina[String(f.codigo)]} />
                    </p>
                    <p className="text-xs text-brinks-muted-foreground tabular-nums">{f.codigo} · {f.fechaLabel}</p>
                  </div>
                  <EstadoBadge estado={f.estado} />
                </div>
                <dl className="mt-3 grid grid-cols-2 gap-2 text-sm">
                  {f.valores.map((v, i) => (
                    <div key={columnas[i].fieldName}>
                      <dt className="text-xs text-brinks-muted-foreground">{columnas[i].etiqueta}</dt>
                      <dd className={cn("tabular-nums", v != null && v !== 0 && esDiferencia(columnas[i].fieldName) && "font-semibold text-brinks-danger")}>
                        {valorLegible(v, columnas[i].formato)}
                      </dd>
                    </div>
                  ))}
                  <div className="col-span-2">
                    <dt className="text-xs text-brinks-muted-foreground">Pendientes</dt>
                    <dd>
                      <CeldaFaltantes alertas={alertasPorCierre ? alertasPorCierre[f.closingId] || [] : undefined} tieneClosing={!!f.closingId} nombres={nombres} />
                    </dd>
                  </div>
                </dl>
              </article>
            ))}
            {filtradas.length === 0 ? (
              <p className="rounded-[12px] border bg-card p-8 text-center text-[13px] text-brinks-muted-foreground">No hay cierres para los filtros seleccionados.</p>
            ) : (
              <div className="overflow-hidden rounded-[12px] border bg-card">
                <Paginacion
                  inicio={inicio}
                  porPagina={porPagina}
                  total={filtradas.length}
                  paginaActual={paginaActual}
                  totalPaginas={totalPaginas}
                  onPorPagina={setPorPagina}
                  onPagina={setPagina}
                />
              </div>
            )}
          </section>
        </>
      )}

      {seleccion.length > 0 && (
        <div className="sticky bottom-3 z-30 mt-3 rounded-[12px] border bg-card/95 p-2 shadow-lg backdrop-blur sm:flex sm:flex-wrap sm:items-center sm:gap-2 sm:p-3">
          <span className="block pb-1.5 pl-1 text-[12px] text-brinks-muted-foreground sm:mr-auto sm:pb-0 sm:pl-2 sm:text-sm">
            {seleccion.length} cierre(s) seleccionado(s)
          </span>
          <div className="flex items-center gap-1 overflow-x-auto sm:contents">
            {puedeExportar ? (
              <Button variant="ghost" size="sm" className="shrink-0 gap-2" onClick={() => exportar(filtradas.filter((f) => seleccion.includes(f.id)))}>
                <Download className="h-4 w-4" /> <span className="whitespace-nowrap">Descargar Excel</span>
              </Button>
            ) : null}
            <Button variant="ghost" size="sm" className="shrink-0 gap-2" onClick={copiarResumen}>
              <Copy className="h-4 w-4" /> <span className="whitespace-nowrap">Copiar resumen</span>
            </Button>
            {puedeExportar ? (
              <Button variant="ghost" size="sm" className="shrink-0 gap-2" onClick={() => setEnviarOficinas(true)}>
                <Mail className="h-4 w-4" />{" "}
                <span className="whitespace-nowrap">{seleccion.length === 1 ? "Enviar correo de oficina" : "Enviar correo de oficinas"}</span>
              </Button>
            ) : null}
            <Button variant="ghost" size="icon" className="shrink-0" aria-label="Cerrar" onClick={() => setSeleccion([])}>
              <X className="h-4 w-4" />
            </Button>
          </div>
        </div>
      )}

      {/* B-89 — mounted only while open, so it asks for the offices' addresses each time it opens. */}
      {enviarOficinas ? (
        <EnviarCorreoOficinas
          open
          onOpenChange={(abierto) => !abierto && setEnviarOficinas(false)}
          token={props.token}
          mantisBusinessId={props.mantisBusiness._id}
          clientCode={clientCode}
          filas={filtradas.filter((f) => seleccion.includes(f.id) && f.closingId)}
          sinPermiso={filtradas
            .filter((f) => seleccion.includes(f.id) && !puede(acceso, PERMISO.EXPORTAR, { cliente: clientCode, oficina: f.codigo, region: f.regional || undefined }))
            .map((f) => f.codigo)}
        />
      ) : null}

      <CierreDetailDialog
        closingId={detalleClosingId}
        panelRow={todasLasFilas.find((f) => f.closingId === detalleClosingId)?.raw
          || (filaExterna?.closing_id === detalleClosingId ? filaExterna : undefined)}
        token={props.token}
        mantisBusinessId={props.mantisBusiness._id}
        onOpenChange={(open) => {
          if (open) return;
          setDetalleClosingId(null);
          setFilaExterna(null);
        }}
        onTrabajarAMano={(closingId, region) => {
          setDetalleClosingId(null);
          setManual({ closingId, aviso: null, region });
        }}
        onCambio={() => setRecarga((n) => n + 1)}
      />

      <CierreManualDialog
        closingId={manual?.closingId || null}
        aviso={manual?.aviso}
        region={manual?.region}
        token={props.token}
        mantisBusinessId={props.mantisBusiness._id}
        onOpenChange={(open) => !open && setManual(null)}
        onChanged={() => setRecarga((n) => n + 1)}
      />
    </div>
  );
}

// B-108 — the mark of an office with open pending notes: how many, and their total on hover. The
// notes themselves are in the closing's detail ("Pendientes de la oficina").
function MarcaNotas({ notas }) {
  if (!notas || !notas.cantidad) return null;
  const texto = notas.cantidad === 1 ? "1 nota pendiente abierta" : `${notas.cantidad} notas pendientes abiertas`;
  return (
    <span
      className="shrink-0 rounded-full border border-brinks-warning-border bg-brinks-warning-soft px-1.5 py-0.5 text-[10px] font-semibold tabular-nums text-brinks-warning"
      title={`${texto} por ${montoLegible(notas.monto)}`}
      aria-label={texto}
    >
      {notas.cantidad} pend.
    </span>
  );
}

// "Documentos faltantes" — `detalle` is `undefined` while the fan-out fetch hasn't reached this
// row yet, `null` if it has no real closing to ask about (a legacy SCHEDULED row), or the real
// response once loaded.
// What the closing is waiting on, said as two different things: what has not arrived (amber —
// "Falta: Saldo inicial de bóveda", the alert's own words on hover) and figures that do not
// balance (red). The alerts come from the list's one batch, so the column fills with the list.
function CeldaFaltantes({ alertas, tieneClosing, nombres }) {
  if (!tieneClosing) return <span className="text-brinks-muted-foreground">—</span>;
  if (alertas === undefined) return <span className="text-brinks-muted-foreground">…</span>;
  const p = pendientesDeAlertas(alertas, (m) => mensajeDeAlerta(nombres, m));
  if (!p.faltantes && !p.descuadres && !p.revisiones) {
    return (
      <span className="flex items-center gap-1.5 text-[12.5px]">
        <span className="h-2 w-2 shrink-0 rounded-full bg-brinks-success" />
        Al día
      </span>
    );
  }
  return (
    <span className="flex min-w-0 flex-col gap-0.5 text-[12.5px]">
      {p.faltantes ? (
        <span className="flex min-w-0 items-center gap-1.5" title={p.mensajesFaltan.join("\n")}>
          <span className="h-2 w-2 shrink-0 rounded-full bg-brinks-warning" />
          <span className="min-w-0 truncate">{etiquetaDeFaltantes(p.faltan)}</span>
        </span>
      ) : null}
      {p.descuadres ? (
        <span className="flex min-w-0 items-center gap-1.5" title={p.mensajesDescuadre.join("\n")}>
          <span className="h-2 w-2 shrink-0 rounded-full bg-brinks-danger" />
          <span className="min-w-0 truncate">{p.descuadres === 1 ? "Descuadre" : `${p.descuadres} descuadres`}</span>
        </span>
      ) : null}
      {p.revisiones ? (
        <span className="flex min-w-0 items-center gap-1.5" title={p.mensajesRevision.join("\n")}>
          <span className="h-2 w-2 shrink-0 rounded-full bg-brinks-warning" />
          <span className="min-w-0 truncate">{p.revisiones === 1 ? "Por revisar" : `${p.revisiones} por revisar`}</span>
        </span>
      ) : null}
    </span>
  );
}

export default connect(selectAuthState)(PanelCierres);
